import { NgModule, ApplicationRef, ErrorHandler } from '@angular/core';
import {
  BrowserModule,
  HammerGestureConfig,
  HAMMER_GESTURE_CONFIG
} from '@angular/platform-browser';
import { HttpModule } from '@angular/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatButtonModule, MatCardModule, MatCheckboxModule, MatChipsModule,
  MatMenuModule, MatToolbarModule, MatDialogModule, MatTabsModule,
  MatIconModule, MatTableModule, MatDatepickerModule, MatNativeDateModule,
  MAT_DIALOG_DEFAULT_OPTIONS, MatAutocompleteModule, MatSelectModule,
  MatProgressSpinnerModule, MatInputModule, MatSidenavModule
} from '@angular/material';
import { OverlayModule } from '@angular/cdk/overlay';
import { FlexLayoutModule } from '@angular/flex-layout';
import { CurrencyMaskModule } from 'ngx-currency-mask';
import { DatePipe } from '@angular/common';
import { environment } from '../environments/environment';

import * as Raven from 'raven-js';

import { NgPipesModule } from 'ngx-pipes';

// Custom Pipes
import {
  PropertyFilterPipe,
  TitleCasePipe
} from './shared/pipes/index';


import { AppComponent } from './app.component';
import { LoginComponent } from './login/login.component';
import { ConfirmRegistrationComponent } from './register/confirm-registration.component';
import { ResetPasswordComponent } from './register/reset-password.component';
import { ForgotUsernameComponent } from './register/forgot-username.component';

// Registration
import { RegisterComponent } from './register/register.component';
import { Step1Component } from './register/step-1.component';
import { Step2Component } from './register/step-2.component';

// Twilio Integration
import { ConsultationRoomComponent } from './consultation-room/consultation-room.component';

// Dashboard
import { DashboardComponent } from './dashboard/dashboard.component';

// Widgets
import { WidgetComponent } from './widgets/widget.component';
import { AverageVisitTimeComponent } from './widgets/average-visit-time/average-visit-time.component';
import { GlobalQueueComponent } from './widgets/global-queue/global-queue.component';
import { PersonalQueueComponent } from './widgets/personal-queue/personal-queue.component';
import { VisitsPerHourComponent } from './widgets/visits-per-hour/visits-per-hour.component';
import { VisitsTodayComponent } from './widgets/visits-today/visits-today.component';
import { EarningsTodayComponent } from './widgets/earnings-today/earnings-today.component';

// Toolbar
import { ToolbarComponent } from './shared/toolbar/toolbar.component';

// Sidebar
import { SidebarComponent } from './shared/sidebar/sidebar.component';
import { MenuLinkComponent } from './shared/menu-link/menu-link.component';
import { MenuToggleComponent } from './shared/menu-toggle/menu-toggle.component';

// Patient Queue
import { PatientQueueComponent } from './patient-queue/patient-queue.component';
import { SkipQueueDialogComponent } from './patient-queue/skip-queue-dialog.component';

// Performance
import { PerformanceComponent } from './performance/performance.component';

// Labs
import { LabsComponent } from './labs/labs.component';

// Prescriptions
import { PrescriptionsComponent } from './prescriptions/prescriptions.component';

// Provider Profile
import { ProfileTabsComponent } from './providers/profile-tabs.component';
import { PersonalProfileComponent } from './providers/personal-profile.component';
import { ProfessionalProfileComponent } from './providers/professional-profile.component';

// Visits
import { VisitComponent } from './visits/visit.component';
import { VisitDetailsComponent } from './visits/visit-details.component';
import { FacesheetComponent } from './visits/facesheet.component';
import { VisitsComponent } from './visits/visits.component';

// Nurse Visits
import { NurseVisitDialogComponent } from './nurse-visits/nurse-visit-dialog.component';

// Shared
import { ConfirmDialogComponent } from './shared/dialogs/confirm-dialog.component';

// Guards
import { AuthGuard } from './guards/auth.guard';

// Services
import { ApiService } from './shared/api.service';
import { AuthenticationService } from './shared/authentication.service';
import { ConsultationRoomService } from './consultation-room/consultation-room.service';
import { ProviderService } from './providers/provider.service';
import { CredentialService } from './credentials/credential.service';
import { SpecialtyService } from './specialties/specialty.service';
import { GenderService } from './genders/gender.service';
import { StateService } from './states/state.service';
import { VisitService } from './visits/visit.service';
import { QueueService } from './patient-queue/queue.service';
import { DataService } from './shared/data.service';
import { PatientService } from './patients/patient.service';
import { SymptomService } from './symptoms/symptom.service';
import { HouseholdService } from './households/household.service';
import { SystemService } from './systems/system.service';
import { MedicalHistoryService } from './patients/medical-history.service';
import { DiagnosisService } from './diagnoses/diagnosis.service';
import { NurseVisitService } from './nurse-visits/nurse-visit.service';
import { MedicalServiceService } from './medical-services/medical-service.service';

import { routing } from './app.routing';

import { NgXtruncateModule } from 'ngx-truncate';

import { NguiMapModule } from '@ngui/map';
import { MomentModule } from 'angular2-moment';
import { CountdownTimer } from './shared/countdown-timer/countdown-timer.component';

// i18n - ngx-translate
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ReliefButtonComponent } from './shared/relief-button/relief-button.component';

if (!window.location.href.indexOf('localhost')) {
  Raven.config('SENTRY_URL', {
    environment: environment.errorTrackingEnv,
    release: environment.applicationVersion
  }).install();
}

export class RavenErrorHandler implements ErrorHandler {
  handleError(err: any): void {
    // Raven.captureException(err);
  }
}

export class MyHammerConfig extends HammerGestureConfig {
  overrides = <any>{
    // override hammerjs default configuration
    pan: { threshold: 5 },
    swipe: {
      velocity: 0.4,
      threshold: 20,
      direction: 31 // /!\ ugly hack to allow swipe in all direction
    }
  };
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

const mapsApiUrl =
  'https://maps.google.com/maps/api/js?libraries=geometry,visualization,places&key=' +
  environment.googleMapsKey;

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatMenuModule,
    MatToolbarModule,
    MatDialogModule,
    MatTabsModule,
    MatIconModule,
    MatTableModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatSidenavModule,
    MatProgressSpinnerModule,
    MatInputModule,
    OverlayModule,
    CurrencyMaskModule,
    NgXtruncateModule,
    NguiMapModule.forRoot({
      apiUrl: mapsApiUrl
    }),
    MomentModule,
    FlexLayoutModule,
    routing,
    NgPipesModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    })
  ],
  declarations: [
    AppComponent,
    LoginComponent,
    ConfirmRegistrationComponent,
    ResetPasswordComponent,
    ForgotUsernameComponent,
    ConsultationRoomComponent,

    ReliefButtonComponent,

    CountdownTimer,

    // Custom Pipes
    PropertyFilterPipe,
    TitleCasePipe,

    // Registration
    RegisterComponent,
    Step1Component,
    Step2Component,

    // Dashboard
    DashboardComponent,

    // Toolbar
    ToolbarComponent,

    // Sidebar
    SidebarComponent,
    MenuLinkComponent,
    MenuToggleComponent,

    // Patient Queue
    PatientQueueComponent,
    SkipQueueDialogComponent,

    // Performance
    PerformanceComponent,

    // Labs
    LabsComponent,

    // Prescriptions
    PrescriptionsComponent,

    // Provider Profile
    ProfileTabsComponent,
    PersonalProfileComponent,
    ProfessionalProfileComponent,

    // Visits
    VisitComponent,
    VisitDetailsComponent,
    FacesheetComponent,
    VisitsComponent,

    // Nurse Visits
    NurseVisitDialogComponent,

    // Shared
    ConfirmDialogComponent,

    // Widgets
    WidgetComponent,
    AverageVisitTimeComponent,
    GlobalQueueComponent,
    PersonalQueueComponent,
    VisitsPerHourComponent,
    VisitsTodayComponent,
    EarningsTodayComponent
  ],
  providers: [
    AuthGuard,
    AuthenticationService,
    ApiService,
    ConsultationRoomService,
    ProviderService,
    CredentialService,
    GenderService,
    StateService,
    SpecialtyService,
    QueueService,
    VisitService,
    DataService,
    PatientService,
    SymptomService,
    HouseholdService,
    SystemService,
    MedicalHistoryService,
    MedicalServiceService,
    DiagnosisService,
    NurseVisitService,
    DatePipe,
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { hasBackdrop: true } },
    { provide: HAMMER_GESTURE_CONFIG, useClass: MyHammerConfig },
    // { provide: ErrorHandler, useClass: RavenErrorHandler }
  ],
  entryComponents: [
    SkipQueueDialogComponent, ConfirmDialogComponent, NurseVisitDialogComponent
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(public appRef: ApplicationRef) {}
}



// WEBPACK FOOTER //
// ./src/app/app.module.ts